<script lang="ts" context="module">
  import Config from "../index.js";
  import sanitize from "sanitize-html";

  const FRIENDLY_DOMAINS: string[] = ["actualitate.net"];

  try {
    FRIENDLY_DOMAINS.push(
      ...(process?.env?.FRIENDLY_DOMAINS.split(",") || []).map(
        (domain: string) => domain.trim().toLowerCase()
      )
    );
  } catch (_e) {}

  export function isValidHostname(value: string): boolean {
    if (typeof value !== "string") {
      return false;
    }

    const validHostnameChars = /^[a-zA-Z0-9-.]{1,253}\.?$/g;
    if (!validHostnameChars.test(value)) {
      return false;
    }

    if (value.endsWith(".")) {
      value = value.slice(0, value.length - 1);
    }

    if (value.length > 253) {
      return false;
    }

    const labels = value.split(".");

    const isValid = labels.every(function (label) {
      const validLabelChars = /^([a-zA-Z0-9-]+)$/g;

      const validLabel =
        validLabelChars.test(label) &&
        label.length < 64 &&
        !label.startsWith("-") &&
        !label.endsWith("-");

      return validLabel;
    });

    return isValid;
  }

  export function getURLRel(url: string | URL): undefined | "nofollow" {
    try {
      const urlo = typeof url === "object" && url.hostname ? url : new URL(url);
      if (!FRIENDLY_DOMAINS.includes(urlo.hostname.toLowerCase())) {
        return "nofollow";
      }
    } catch (e) {
      return;
    }
  }

  export function sanitizeHTML(text: string, allowedTags?: string[]) {
    if (!allowedTags) {
      allowedTags = [
        "address",
        "article",
        "aside",
        "footer",
        "header",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "hgroup",
        "main",
        "nav",
        "section",
        "blockquote",
        "dd",
        "div",
        "dl",
        "dt",
        "figcaption",
        "figure",
        "hr",
        "li",
        "main",
        "ol",
        "p",
        "pre",
        "ul",
        "a",
        "abbr",
        "b",
        "bdi",
        "bdo",
        "br",
        "cite",
        "code",
        "data",
        "dfn",
        "em",
        "i",
        "kbd",
        "mark",
        "q",
        "rb",
        "rp",
        "rt",
        "rtc",
        "ruby",
        "s",
        "samp",
        "small",
        "span",
        "strong",
        "sub",
        "sup",
        "time",
        "u",
        "var",
        "wbr",
        "caption",
        "col",
        "colgroup",
        "table",
        "tbody",
        "td",
        "tfoot",
        "th",
        "thead",
        "tr",
      ];
    }

    return sanitize(text, {
      allowedAttributes: {
        a: ["rel", "href", "target", "title"],
        iframe: [
          "src",
          "width",
          "height",
          "frameborder",
          "allow*",
          "frameborder",
          "style",
          "class",
        ],
      },
      allowedTags,

      transformTags: {
        a: (tagName: any, attribs: any) => {
          try {
            if (attribs.href?.startsWith("/")) {
              attribs.href = `https://${Config.domain}${attribs.href}`;
            }

            const x = new URL(attribs.href);

            if (!isValidHostname(x.hostname)) {
              throw new Error("invalid hostname");
            }

            const rel = getURLRel(x);

            if (!attribs.rel && rel) {
              attribs.rel = rel;
            }
          } catch (e) {
            return {
              tagName: "span",
              text: attribs.text,
              attribs: {},
            };
          }

          return {
            tagName,
            attribs,
          };
        },

        iframe: (tagName: any, attribs: any) => {
          const na = { ...attribs };

          let facebook = false;
          if (attribs?.src?.startsWith("https://www.facebook.com")) {
            na.src = na.src.replace(/\&width\=\d{1,}/i, "&width=390");
            facebook = true;
            na.class = "facebook-iframe";
          }

          if (!facebook && na.width && na.height) {
            na.class = na.class ? `${na.class} responsive` : "responsive";

            let w = parseInt(na.width, 10);
            let h = parseInt(na.height, 10);
            if (!isNaN(w) && !isNaN(h)) {
              na.style = `--aspectRatio: ${w / h}; ${na.style || ""}`;
            }
          }

          return {
            tagName,
            attribs: na,
          };
        },
      },
    });
  }

  export function addUtmToURL(
    url: string,
    utm:
      | null
      | undefined
      | {
          source?: string;
          medium?: string;
          campaign?: string;
          content?: string;
          term?: string;
        }
  ): string {
    if (!utm || !url || !Object.values(utm).length) {
      return url;
    }

    if (url.startsWith("/") || url.startsWith(`https://${Config.domain}`)) {
      return url;
    }

    const urlA = url.split("?");

    const q = new URLSearchParams(urlA[1] || "");
    Object.keys(utm).forEach((k) => {
      if (utm[k] && !q.has(k)) {
        q.set(`utm_${k}`, utm[k]);
      }
    });

    return `${urlA[0]}?${q.toString()}`;
  }
</script>
